import { useEffect, useRef, type RefObject } from 'react';

/**
 * El lector de códigos USB, entre las teclas.
 *
 * A USB barcode scanner is a keyboard: it types the code and presses Enter into whatever has the
 * cursor. On a screen whose way in is the scanner that is the wrong place as often as not: the
 * cursor was left in the invoice number, or in a line's quantity, and the code went in there
 * instead of finding its product (the owner, 2026-10-02). What gives a scanner away is its speed:
 * a few milliseconds a key, where somebody typing as fast as they can takes eighty or more, and
 * never six keys in a row, then Enter, under fifty each.
 */

/** The longest a scanner leaves between two keys, Enter included. Bluetooth ones are the slow ones. */
export const ENTRE_TECLAS_MS = 50;
/** The shortest barcode, UPC-E's six digits. Fewer keys at that speed is two fingers landing at once. */
export const LARGO_MINIMO = 6;

/** Keys typed one after another at a scanner's speed. */
export interface Rafaga {
  texto: string;
  /** When the last of them came (`KeyboardEvent.timeStamp`, which is when it was pressed, not handled). */
  ultima: number;
}

/** A printable key: one more in the burst, or the first of a new one when the last came too long before. */
export function teclear(rafaga: Rafaga | null, tecla: string, cuando: number): Rafaga {
  return rafaga && cuando - rafaga.ultima <= ENTRE_TECLAS_MS
    ? { texto: rafaga.texto + tecla, ultima: cuando }
    : { texto: tecla, ultima: cuando };
}

/** At Enter: the code a scanner typed, or null when it was somebody at the keyboard. */
export function codigoLeido(rafaga: Rafaga | null, cuando: number): string | null {
  if (!rafaga || cuando - rafaga.ultima > ENTRE_TECLAS_MS) return null;
  const codigo = rafaga.texto.trim();
  return codigo.length >= LARGO_MINIMO ? codigo : null;
}

type Campo = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const esCampo = (el: Element | null): el is Campo =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;

/**
 * A sheet over the screen, or the camera: what it asks is answered before anything else goes in.
 * Not a sheet that asks nothing and that the next scan ends (`data-cede-al-lector`): the till's
 * receipt, which the next customer's first scan closes, as «Siguiente venta» would.
 */
const hayHoja = () => document.querySelector('div.surge:not([data-cede-al-lector]), [role="dialog"]') !== null;

/** What a field held, and where its cursor was, before a burst's first key went into it. */
interface Foto {
  campo: Campo;
  valor: string;
  desde: number | null;
  hasta: number | null;
}

function fotografiar(el: Element | null): Foto | null {
  if (!esCampo(el)) return null;
  let desde: number | null = null;
  let hasta: number | null = null;
  try {
    if (!(el instanceof HTMLSelectElement)) ({ selectionStart: desde, selectionEnd: hasta } = el);
  } catch {
    // A date has no cursor to speak of.
  }
  return { campo: el, valor: el.value, desde, hasta };
}

/**
 * The field as it was. Through the value's own setter and an input event, the way a key would
 * have done it, so the screen's state hears of it too and does not put the code back.
 */
function devolver({ campo, valor, desde, hasta }: Foto) {
  if (campo.value === valor) return;
  Object.getOwnPropertyDescriptor(Object.getPrototypeOf(campo) as object, 'value')?.set?.call(campo, valor);
  campo.dispatchEvent(new Event('input', { bubbles: true }));
  campo.dispatchEvent(new Event('change', { bubbles: true }));
  if (desde === null || hasta === null || campo instanceof HTMLSelectElement) return;
  try {
    campo.setSelectionRange(desde, hasta);
  } catch {
    // Not every kind of field takes a cursor back.
  }
}

/**
 * Everything a USB scanner types on this screen goes to `alLeer`, wherever the cursor was. The code
 * is taken back out of the field it went into, which is left as it was, cursor and all; typed into
 * `caja` itself, it is taken out of that too, and `alLeer` puts it where it belongs.
 *
 * With nothing under the cursor, the first key, of a code or of a name typed on a keyboard, puts
 * the cursor in `caja` and the rest lands there, as at the till: where `caja` is a search, which a
 * name is typed into as often as a code is scanned. Not with `enrutar: false`, where it is a
 * barcode field and a name typed belongs anywhere but there; a scan is read all the same. With a
 * sheet or the camera open, a scan goes nowhere, Enter included: what is asked is answered first.
 * A sheet that the next scan ends (`hayHoja`) is not in the way; closing it is the screen's job.
 * Any other field made for codes (`data-codigo`) keeps what is scanned into it. Behind the PIN door,
 * the keys are the door's.
 */
export function useLectorUsb(
  caja: RefObject<HTMLInputElement | null>,
  alLeer: (codigo: string) => void,
  { activo = true, enrutar = true }: { activo?: boolean; enrutar?: boolean } = {},
) {
  // The listener outlives any one render, so it reads this instead of closing over the props.
  const leer = useRef(alLeer);
  leer.current = alLeer;

  useEffect(() => {
    if (!activo) return;
    let rafaga: Rafaga | null = null;
    /** Where the burst's keys went, and the field it was, as it was. */
    let donde: Element | null = null;
    let foto: Foto | null = null;

    function alTeclear(e: KeyboardEvent) {
      // A scanner presses Shift for each capital letter of a code with letters in it.
      if (e.key === 'Shift' || e.key === 'CapsLock') return;
      const propia = caja.current;
      if (!propia || propia.closest('[inert]') || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) {
        rafaga = null;
        return;
      }
      if (e.key === 'Enter') {
        const codigo = codigoLeido(rafaga, e.timeStamp);
        const [en, como] = [donde, foto];
        rafaga = donde = foto = null;
        if (codigo === null || (en !== propia && en?.closest('[data-codigo]'))) return;
        // Enter pressed nothing (a focused button, a sheet's) and the box's own Enter is this.
        e.preventDefault();
        e.stopImmediatePropagation();
        if (como) devolver(como);
        if (!hayHoja()) leer.current(codigo);
        return;
      }
      if (e.key.length !== 1 || e.repeat) {
        rafaga = null;
        return;
      }
      // Not Space, which presses a focused button for somebody working by keyboard.
      if (enrutar && !esCampo(document.activeElement) && e.key !== ' ' && !hayHoja()) propia.focus();
      const nueva = !rafaga || e.timeStamp - rafaga.ultima > ENTRE_TECLAS_MS;
      rafaga = teclear(rafaga, e.key, e.timeStamp);
      if (nueva) {
        donde = document.activeElement;
        foto = fotografiar(donde);
      }
    }
    // Capturing, ahead of everything else: by the time the field's own handlers see Enter it is too late.
    window.addEventListener('keydown', alTeclear, true);
    return () => window.removeEventListener('keydown', alTeclear, true);
  }, [caja, activo, enrutar]);
}
